import React, { useState } from 'react';
import { AppState } from '../../store/state';
import { AppDispatch } from '../../store/store';
import { connect } from 'react-redux';
import {
  selectErrorState,
  selectKeywordsLevels,
  selectKeywordState,
  selectTrackUsingKeywords,
} from '../../store/selectors';
import { Button } from '../../components/ui';
import keywordSlice from '../../store/keyword/slice';
import KeywordsTree from '../../components/KeywordsTree/KeywordsTree';
import styled, { ThemeProvider } from 'styled-components';
import { DataNode, KeywordsComplexTreeAdapter } from '../../helpers/keywords';
import { TreeItemIndex } from 'react-complex-tree';
import { downloadFile, getHref } from '../../helpers/download';
import { AddButton } from '../../components/ui/inputs/AddButton/AddButton';
import useTheme from '@material-ui/core/styles/useTheme';
import Download2 from '../../icons/Download2';
import Maximize from '../../icons/Expand';
import Collapse from '../../icons/Collapse';
import { useTranslation } from 'react-i18next';
import Key from '../../icons/Key';
import { HowItWorksButton } from '../../components/ui/inputs/HowItWorksButton/HowItWorksButton';
import { Switch } from '@progress/kendo-react-inputs';
import DocumentationSidePanel from '../../components/ui/utils/DocumentationSidePanel/DocumentationSidePanel';
import AddKeywordsDialog from '../../components/dialogs/AddKeywordsDialog/AddKeywordsDialog';

interface Props {
  keywordState: KeywordState;
  updateKeywordTreeAction: (data: KeywordTreeDTO) => void;
  renameKeywordAction: (data: KeywordDTO) => void;
  deleteKeywordAction: (data: number) => void;
  activateKeywordsAction: (areUsed: boolean) => void;
  trackUsingKeywords: boolean | null;
  keywordsLevels: KeywordsLevels;
}

const SettingsKeywordsPage: React.FC<Props> = ({
  keywordState,
  updateKeywordTreeAction,
  renameKeywordAction,
  deleteKeywordAction,
  activateKeywordsAction,
  trackUsingKeywords,
  keywordsLevels,
}) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>([]);
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);
  const theme = useTheme();

  const { keywordById, keywordTree } = keywordState;

  const hasKeywords = Object.keys(keywordTree).length > 0;
  const hasChildren = Object.values(keywordTree).filter((children) => children.length > 0).length > 0;

  const openDialog = () => {
    setDialog(<AddKeywordsDialog handleClose={() => setDialog(undefined)} />);
  };

  const getParentIds = (): number[] => {
    return Object.keys(keywordTree).map((keywordId) => parseInt(keywordId));
  };

  return (
    <ThemeProvider theme={theme}>
      {trackUsingKeywords === null ? (
        <div className="h-full flex justify-center items-center">
          <StyledMessageWrapper className="flex justify-center items-center flex-col mb-6">
            <StyledIcon>
              <Key />
            </StyledIcon>
            <h2 className="text-xl font-bold w-max mt-3">{t('Organise your content with smart keywords')}</h2>
            <p className="mt-3 mb-6 text-center">
              {t('Add keywords to improve categorisation and make your content easier to find and manage.')}
            </p>

            <div className="flex items-center">
              <StyledAddNewKeywordWrapper className="flex items-center">
                <StyledButton
                  className="w-32 place-self-start mr-6"
                  variant="outlined"
                  onClick={() => activateKeywordsAction(false)}
                >
                  <span className="px-14">{t('Deactivate')}</span>
                </StyledButton>
                <StyledButton className="w-32 place-self-start" onClick={openDialog}>
                  <span className="px-14">{t('Add Keywords')}</span>
                </StyledButton>
              </StyledAddNewKeywordWrapper>
            </div>
          </StyledMessageWrapper>
        </div>
      ) : (
        <div className="w-full flex" style={{ flexGrow: 1 }}>
          <div className="text-sm w-full">
            <div className="flex items-center mb-6 max-w-4xl">
              <StyledButtonWrapper className="flex" style={{ flexGrow: 1 }}>
                <AddButton
                  label={t('Add Keywords')}
                  disabled={!trackUsingKeywords && hasKeywords}
                  onClick={openDialog}
                />

                <HowItWorksButton src="https://embed.app.guidde.com/playbooks/1XgWDBii96rb6VDBwRpKNa" />

                <div className="flex justify-end items-center" style={{ flexGrow: 1 }}>
                  {keywordsLevels > 1 && (
                    <Button
                      className="expand-button ml-8"
                      onClick={() => {
                        const parentIdsTransform = getParentIds().map((dataId) => dataId.toString());
                        setExpandedItems(expandedItems.length > 0 ? [] : parentIdsTransform);
                      }}
                      disabled={!hasKeywords || !hasChildren || !trackUsingKeywords}
                      variant="text"
                      icon={expandedItems.length > 0 ? <Collapse /> : <Maximize />}
                    >
                      <span className="ml-4 block w-20 text-left">
                        {expandedItems.length > 0 ? t('Collapse all') : t('Expand all')}
                      </span>
                    </Button>
                  )}

                  <Button
                    className="export-button ml-8"
                    disabled={!hasKeywords || !trackUsingKeywords}
                    onClick={() => downloadFile(getHref('/web/keywords/export'))}
                    variant="text"
                    icon={<Download2 />}
                  >
                    <span className="ml-4">{t('Export Keywords')}</span>
                  </Button>

                  <div className="flex items-center justify-end space-x-2 ml-7">
                    <Switch
                      onLabel=""
                      offLabel=""
                      checked={trackUsingKeywords}
                      onChange={(event) => activateKeywordsAction(event.value)}
                      disabled={!hasKeywords}
                    />
                    <span className="font-medium">{!trackUsingKeywords ? t('Keywords OFF') : t('Keywords ON')}</span>
                  </div>
                </div>
              </StyledButtonWrapper>
            </div>

            {hasKeywords && trackUsingKeywords && keywordsLevels > 1 ? (
              <p className="mb-2">{t('You can drag and drop a topic keyword into an area keyword.')}</p>
            ) : (
              <p style={{ marginBottom: '3.3rem' }}></p>
            )}

            {hasKeywords && (
              <KeywordsTree
                dataTree={new KeywordsComplexTreeAdapter().transform(keywordById, keywordTree, keywordsLevels)}
                canDragAndDrop={keywordsLevels > 1}
                onDrop={(items) => {
                  const reverseDataTree = new KeywordsComplexTreeAdapter().reverse(
                    items as { [index: string]: DataNode },
                  );
                  updateKeywordTreeAction({ tree: reverseDataTree });
                }}
                onRenameItem={(id: number, name: string) => {
                  renameKeywordAction({ id, name, parent_id: keywordById[id].parent_id });
                }}
                onDelete={deleteKeywordAction}
                expandItemsValue={expandedItems}
                handleExpandedItemsChange={(newValue) => setExpandedItems(newValue)}
                disabled={!trackUsingKeywords}
              />
            )}
          </div>

          <DocumentationSidePanel>
            <h3>Lorem ipsum dolor sit amet consectetur in arcu metus.</h3>
            <p>
              Lorem ipsum dolor sit amet consectetur. Risus mi ultrices dolor id tellus mi tellus. Etiam velit mi in hac
              nunc cras venenatis rhoncus. In cras facilisi feugiat sed nibh posuere sollicitudin faucibus sit. Netus
              amet blandit curabitur arcu. Lobortis ridiculus sapien augue quis amet faucibus.
            </p>
            <p>
              Nibh sapien sit diam aliquam eu adipiscing viverra faucibus at. Placerat congue pellentesque lobortis
              gravida. Ut aliquet sit mattis pharetra pellentesque hendrerit euismod tortor. Natoque ipsum adipiscing
              ultricies volutpat ultrices lectus maecenas malesuada. Pulvinar vel ac et velit. Adipiscing est bibendum
              quis quam.
            </p>
            <p>
              Volutpat ultrices eget eleifend scelerisque donec id id tristique. Et laoreet et enim ut. Volutpat purus
              nunc ipsum nisl bibendum id suspendisse ridiculus cursus.
            </p>
          </DocumentationSidePanel>
        </div>
      )}
      {dialog}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  keywordState: selectKeywordState(state),
  trackUsingKeywords: selectTrackUsingKeywords(state),
  keywordsLevels: selectKeywordsLevels(state),
  validationErrors: selectErrorState(state).validationErrors,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateKeywordTreeAction: (data: KeywordTreeDTO) => dispatch(keywordSlice.actions.UPDATE_TREE(data)),
  renameKeywordAction: (data: KeywordDTO) => dispatch(keywordSlice.actions.RENAME_KEYWORD(data)),
  deleteKeywordAction: (data: number) => dispatch(keywordSlice.actions.DELETE_KEYWORD(data)),
  activateKeywordsAction: (areUsed: boolean) => dispatch(keywordSlice.actions.ACTIVATE(areUsed)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsKeywordsPage);

const StyledButtonWrapper = styled.div`
  .expand-button,
  .export-button {
    color: ${(props) => props.theme.palette.primary.main};
    padding-left: 0;
    padding-right: 0;
  }
  .addButton {
    margin-left: 0;
  }
`;

const StyledMessageWrapper = styled.div`
  max-width: 32rem;

  h2 {
    font-size: 1.45rem;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
`;

const StyledIcon = styled.div`
  svg {
    width: 7.5rem;
    height: 7.5rem;
  }
`;

const StyledAddNewKeywordWrapper = styled.div`
  .addButton {
    margin-left: 0;

    svg {
      width: 3rem;
    }
    span {
      font-size: 1.1rem;
      font-weight: 700;
      margin-left: 0.3rem;
    }
  }
`;

const StyledButton = styled(Button)`
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  padding-left: 5.5rem;
  padding-right: 5.5rem;
  font-size: 0.975rem;
`;
